import React from 'react'
import {
  makeStyles,
  createStyles,
  Box,
  Button,
  CircularProgress,
  TextField,
  InputLabel,
} from '@material-ui/core'

import { toast } from 'react-toastify'
import { useSelector, useDispatch } from 'react-redux'
import { updatePhone, updateEmail, changeUserInfo, getUserInfo } from '../../store/slices/authSlice'
import { Formik } from 'formik'
import { omit } from 'lodash'
import { useTranslation } from 'react-i18next'
import { DIALING_CODES } from '../../utils/constant'
import CodeButton from './CodeButton'
import { oriSchema } from '../../utils/schema'
import { useTranslateFormErrors } from '../../hooks'
import DialingCodeSelector from './DialingCodeSelector'

const useStyles = makeStyles((theme) =>
  createStyles({
    select: {
      padding: theme.spacing(0.5, 0),
      '&:focus': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    withShrinkInput: {
      marginRight: theme.spacing(2),
      marginTop: theme.spacing(0.25),
    },

    label: {
      minWidth: theme.spacing(10),
      display: 'flex',
      flexDirection: 'row-reverse',
      justifyContent: 'flex-end',
      '& .MuiInputLabel-outlined': {
        display: 'flex',
        flexDirection: 'row-reverse',
      },
      '& .MuiFormLabel-asterisk': {
        color: theme.palette.secondary.main,
      },
    },
    autoMinWidth: {
      minWidth: 'auto',
    },
    helperText: {
      marginLeft: 0,
      marginBottom: theme.spacing(0.5),
    },
  }),
)

const ChangePhoneForm = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const field = props.field
  const { updatePhoneStatus, updateEmailStatus } = useSelector((state) => state.auth)

  const initialValues = {
    dialingCode: props.dialingCode || DIALING_CODES[0].value,
    phone: props.phone || '',
    email: props.email || '',
    code: '',
  }
  const handleCancel = (values) => {
    props.onConfirm && props.onConfirm(values)
  }

  const schema = oriSchema(t).pick(['email', 'dialingCode', 'phone', 'code'])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema.omit(field === 'email' ? ['phone', 'dialingCode'] : ['email'])}
      onSubmit={async (values) => {
        if ((field === 'email' ? updateEmailStatus : updatePhoneStatus) === 'pending') return
        const pureValues = omit(values, [field === 'email' ? 'phone' : 'email'])
        if (field === 'email') {
          await dispatch(updateEmail({ ...pureValues })).unwrap()
        } else {
          await dispatch(updatePhone({ ...pureValues })).unwrap()
        }
        dispatch(changeUserInfo(omit(pureValues, ['code'])))
        dispatch(getUserInfo())
        toast.success(t('common.changed_successfully', { field: `$t(form.${field})` }))
        handleCancel(pureValues)
      }}
    >
      {({ values, setFieldTouched, setFieldValue, touched, errors, handleChange, handleSubmit }) => {
        const isError = (field) => touched[field] && Boolean(errors[field])

        const errorText = (field) => touched[field] && errors[field]
        useTranslateFormErrors(errors, touched, setFieldTouched)

        const onBeforeGetCode = async () => await schema.validateAt([field], values)

        const onGetCodeError = () => setFieldTouched(field)

        return (
          <form onSubmit={handleSubmit} noValidate>
            <InputLabel className={classes.label} required>
              {field === 'email' ? t('form.address_email') : t(`form.${field}`)}
            </InputLabel>
            <TextField
              id={`${field}-field`}
              name={field}
              margin="normal"
              fullWidth
              className={classes.textField2}
              value={values[field]}
              variant="outlined"
              onChange={handleChange}
              error={isError(field)}
              helperText={errorText(field)}
              FormHelperTextProps={{
                classes: {
                  root: classes.helperText,
                },
              }}
              InputProps={{
                startAdornment: field === 'phone' && (
                  <DialingCodeSelector
                    value={values.dialingCode}
                    onChange={(dialingCode) => setFieldValue('dialingCode', dialingCode)}
                  />
                ),
              }}
            />
            <CodeButton
              type={field}
              action="update_user_info"
              params={
                field === 'email'
                  ? { [field]: values[field] }
                  : {
                      dialingCode: values.dialingCode,
                      [field]: values[field],
                    }
              }
              onBeforeGetCode={onBeforeGetCode}
              onGetCodeError={onGetCodeError}
            />

            <InputLabel classes={{ root: classes.inputLabel }} shrink>
              {t('form.input_code')}
            </InputLabel>

            <TextField
              id="code"
              margin="normal"
              name="code"
              fullWidth
              variant="outlined"
              value={values.code}
              onChange={handleChange}
              error={isError('code')}
              helperText={errorText('code')}
              FormHelperTextProps={{
                classes: {
                  root: classes.helperText,
                },
              }}
            />
            <Box mt={6} mb={2}>
              <Button type="submit" fullWidth variant="contained" color="secondary">
                {(field === 'email' ? updateEmailStatus : updatePhoneStatus) === 'pending' ? (
                  <CircularProgress color="inherit" size={24} />
                ) : (
                  t('common.save_change')
                )}
              </Button>
            </Box>
            <Button variant="outlined" fullWidth color="default" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
          </form>
        )
      }}
    </Formik>
  )
}

export default ChangePhoneForm
